import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import {
  RawMaterial,
  LabourItem,
  FixedItem,
  VariableOverhead,
  IndirectLabourItem,
} from '../raw-materials-component/raw-materials.component';

interface CostCategory {
  title: string;
  amount: number;
  percentage: number;
  iconClass: string;
  iconColor: string;
  progressClass: string;
}

@Component({
  standalone: true,
  selector: 'app-cost-breakdown',
  imports: [CommonModule],
  templateUrl: './cost-breakdown.component.html',
  styleUrls: ['./cost-breakdown.component.css'],
})
export class CostBreakdownComponent implements OnInit, OnChanges {
  @Input() directMaterials: RawMaterial[] = [];
  @Input() directLabor: LabourItem[] = [];
  @Input() indirectLabor: IndirectLabourItem[] = [];
  @Input() fixedOverheads: FixedItem[] = [];
  @Input() variableOverheads: VariableOverhead[] = [];

  totalCost = 0;
  categories: CostCategory[] = [];

  ngOnInit(): void {
    this.calculateCosts();
  }

  ngOnChanges(_: SimpleChanges): void {
    this.calculateCosts();
  }

  private calculateCosts(): void {
    const materialCost = this.sumUnitCosts(this.directMaterials);

    const directLabourCost = this.sumUnitCosts(this.directLabor);
    const indirectLabourCost = this.sumUnitCosts(this.indirectLabor);
    const labourCost = directLabourCost + indirectLabourCost;

    const fixedOverheadCost = this.fixedOverheads.reduce(
      (sum, item) => sum + item.unitCost,
      0
    );

    const variableOverheadCost = this.sumUnitCosts(this.variableOverheads);

    this.totalCost =
      materialCost + labourCost + fixedOverheadCost + variableOverheadCost;

    this.categories = [
      {
        title: 'Direct Materials',
        amount: materialCost,
        percentage: this.calculatePercentage(materialCost),
        iconClass: 'bi bi-tools',
        iconColor: 'IconBlue',
        progressClass: 'progress-primary',
      },
      {
        title: 'Labour',
        amount: labourCost,
        percentage: this.calculatePercentage(labourCost),
        iconClass: 'bi bi-briefcase',
        iconColor: 'IconGreen',
        progressClass: 'progress-light-blue',
      },
      {
        title: 'Fixed Overheads',
        amount: fixedOverheadCost,
        percentage: this.calculatePercentage(fixedOverheadCost),
        iconClass: 'bi bi-building',
        iconColor: 'IconRed',
        progressClass: 'progress-muted-teal',
      },
      {
        title: 'Variable Overheads',
        amount: variableOverheadCost,
        percentage: this.calculatePercentage(variableOverheadCost),
        iconClass: 'bi bi-activity',
        iconColor: 'IconPurple',
        progressClass: 'progress-desaturated',
      },
    ];
  }

  /** Sum unitCost on any array of items that expose unitCost */
  private sumUnitCosts(items: Array<{ unitCost?: number }>): number {
    return items.reduce((sum, item) => sum + (item.unitCost ?? 0), 0);
  }

  private calculatePercentage(amount: number): number {
    return this.totalCost > 0 ? (amount / this.totalCost) * 100 : 0;
  }
}
